<script setup lang="ts">
import { computed } from 'vue'

/** 1–999 as-is; 1000+ as compact k (e.g. 1.4k); 1M+ as M when needed. */
function formatNotificationCount(n: number): string {
  if (n < 1000) return String(n)
  if (n < 1_000_000) {
    const k = n / 1000
    if (k < 10) {
      const rounded = Math.round(k * 10) / 10
      const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
      return `${str}k`
    }
    return `${Math.round(k)}k`
  }
  const m = n / 1_000_000
  if (m < 10) {
    const rounded = Math.round(m * 10) / 10
    const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
    return `${str}M`
  }
  return `${Math.round(m)}M`
}

/**
 * Red pill on the top-right of the slotted icon, overlapping it.
 * Uses inline positioning so the badge cannot fall back to in-flow layout beside the icon.
 */
const props = withDefaults(
  defineProps<{
    count?: number | null
    title?: string
    /** Optional classes on the outer wrapper (e.g. mr-3 from sidebar) */
    contentClass?: string
  }>(),
  {
    count: null,
    title: undefined,
    contentClass: '',
  }
)

const showBadge = computed(() => (props.count ?? 0) > 0)

const badgeText = computed(() => formatNotificationCount(props.count ?? 0))

/** Corner-anchored badge: sits on the icon’s top-right point (half in / half out), not centered over the glyph. */
const badgeStyle = {
  position: 'absolute' as const,
  top: '0',
  right: '0',
  transform: 'translate(50%, -50%)',
  zIndex: 10,
}

const wrapperStyle = {
  position: 'relative' as const,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'visible' as const,
}

const isSingleDigit = computed(() => badgeText.value.length === 1)
</script>

<template>
  <span class="inline-flex shrink-0 items-center justify-center" :class="contentClass">
    <span class="shrink-0 overflow-visible" :style="wrapperStyle">
      <slot />
      <span
        v-if="showBadge"
        aria-hidden="true"
        :style="badgeStyle"
        class="pointer-events-none inline-flex items-center justify-center whitespace-nowrap rounded-full bg-red-600 text-[10px] font-bold tabular-nums leading-none text-white shadow ring-1 ring-white dark:ring-gray-800 dark:bg-red-600"
        :class="[
          isSingleDigit ? 'h-4 w-4' : 'h-4 min-w-[1rem] px-1 sm:px-1.5',
          badgeText.length > 3 ? 'text-[9px] px-1' : '',
        ]"
        :title="title"
      >
        {{ badgeText }}
      </span>
    </span>
  </span>
</template>
